import { useEffect, useState } from 'react';
import { Form, FormikProvider, useFormik, useFormikContext } from 'formik';
import { toFormikValidate, toFormikValidationSchema } from 'zod-formik-adapter';
import { Label } from '@antimatterhq/ui/components/label';
import Spacer from '@antimatterhq/ui/components/spacer';
import { PresetHeading } from '../../_partials/PresetHeading';
import { FormError } from '@/components/form/form';
import { getSourceInitialValues, setSourceFormValues } from '@/lib/datasource';
import {
  SourceSchema,
  SourceSchemaFormValues,
} from '@/zod/schemas/dataSource/Source';
import { DataSourceNavigationButtons } from '@/components/button/dataSourceNavigation/DataSourceNavigationButtons';
import {
  DatasourceSubmitType,
  useConfigDataSource,
} from '@/contexts/ConfigDataSourceContext';
import { FormInput } from '@/components/form/input/FormInput';
import { TwoColumnLayout } from '@/components/container';

export type DataSourceFormProps = {
  submitAction: DatasourceSubmitType;
  setSubmitAction: (action: DatasourceSubmitType) => void;
};

const DataSourceForm = ({
  submitAction,
  setSubmitAction,
}: DataSourceFormProps) => {
  const { handlePreviousStep, isSaving, isReviewButtonVisible, setFormik } =
    useConfigDataSource();

  const { values, handleChange, handleBlur, dirty, handleSubmit } =
    useFormikContext<SourceSchemaFormValues>();

  useEffect(() => {
    setFormik({ handleSubmit, dirty });
  }, [handleSubmit, dirty, setFormik]);

  return (
    <Form data-testid="datasource-form">
      <Label
        htmlFor="source"
        required
        data-testid="source-label"
        id="source-label"
      >
        Source
      </Label>
      <FormInput
        id="source"
        aria-labelledby="source-label"
        name="source"
        value={values.source ?? undefined}
        onChange={handleChange}
        onBlur={handleBlur}
        autoComplete="off"
        disabled={isSaving}
        data-testid="source-input"
      />
      <FormError name="source" data-testid="source-error" />

      <Spacer size="lg" />

      <Label
        htmlFor="sourceType"
        required
        data-testid="source-type-label"
        id="source-type-label"
      >
        Source Type
      </Label>
      <FormInput
        id="sourceType"
        name="sourceType"
        value={values.sourceType ?? undefined}
        onChange={handleChange}
        onBlur={handleBlur}
        autoComplete="off"
        disabled={isSaving}
        data-testid="source-type-input"
        aria-labelledby="source-type-label"
      />
      <FormError name="sourceType" data-testid="source-type-error" />

      <Spacer size="2xl" />

      <DataSourceNavigationButtons
        disabled={isSaving}
        goToPreviousStep={handlePreviousStep}
        handlePreviousStep={() => setSubmitAction('previous')}
        handleNextStep={() => setSubmitAction('next')}
        handleReview={() => setSubmitAction('review')}
        loadingNext={submitAction === 'next' && isSaving}
        loadingReview={submitAction === 'review' && isSaving}
        isReviewButtonVisible={isReviewButtonVisible}
        data-testid="datasource-navigation-buttons"
      />
    </Form>
  );
};

export default function DataSource() {
  const { dataSource, handleNextStep, handleReview, save, handlePreviousStep } =
    useConfigDataSource();

  const [submitAction, setSubmitAction] = useState<DatasourceSubmitType>('');
  const formik = useFormik({
    initialValues: getSourceInitialValues(dataSource),
    validationSchema: toFormikValidationSchema(SourceSchema),
    validate: toFormikValidate(SourceSchema),
    onSubmit: async (newValues) => {
      const newDataSource = setSourceFormValues(dataSource, newValues);
      const result = await save(newDataSource);

      if (!result) {
        return;
      }

      // go to next step
      if (submitAction === 'next') {
        handleNextStep();
      } else if (submitAction === 'review') {
        handleReview();
      } else if (submitAction === 'previous') {
        handlePreviousStep();
      }
    },
  });

  return (
    <TwoColumnLayout.Root data-testid="datasource-step-container">
      <TwoColumnLayout.LeftColumn>
        <PresetHeading />
        <FormikProvider value={formik}>
          <DataSourceForm
            submitAction={submitAction}
            setSubmitAction={setSubmitAction}
          />
        </FormikProvider>
      </TwoColumnLayout.LeftColumn>
      <TwoColumnLayout.RightColumn> </TwoColumnLayout.RightColumn>
    </TwoColumnLayout.Root>
  );
}
